import { useMutation, useQuery } from "@apollo/client/react";
import { Box, Button } from "@mui/material";
import MuiDialogActions from "@mui/material/DialogActions";
import MuiDialogContent from "@mui/material/DialogContent";
import MuiDialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import Radio, { RadioProps } from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import { styled, useTheme, Theme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import { useAppDispatch } from "store";
import clsx from "clsx";
import { RenderHTML, Dialog, Action } from "components";
import { SetStateAction, useEffect, useState } from "react";
import { SaveSurveyInput, SURVEY_QUESTION_TYPE, SurveyType } from "utils/graphql/Global";
import * as SurveyTypes from "utils/graphql/SurveyData";
import { SAVE_SURVEY, SURVEY_DATA } from "utils/queries";
import { Loader } from "components/loader/loader";
import { useFocusStyles } from "hooks/focusBorder";
import { useTranslation } from "react-i18next";
import portalSettingsManager from "utils/portalSettingsManager";
import { useViewport } from "hooks/useViewport";
import { SURVEY_OPTIONS_NEGATIVE_RESPONSE } from "utils/constants";
import errorIcon from "assets/icons/icon-triangle.svg";
import { removeSpaceAndSpecialCharacters } from "utils/helpers";

const TextAreaComponent = styled("textarea")<{ isMobile?: boolean }>(({ isMobile }) => ({
  padding: "5px 5px",
  width: isMobile ? "200px" : "275px",
  fontSize: "12px",
}));

const ErrorMessage = styled("div")(() => ({
  background: "#d0021b",
  color: "#fff",
  fontSize: 11,
  padding: "5px 20px",
  marginLeft: 2,
  marginBottom: 5,
  marginTop: 0,
  width: "calc(100% - 59%)",
}));

const DialogTitle = styled(MuiDialogTitle)({
  justifyContent: "flex-end",
  padding: 0,
  "& svg": {
    float: "right",
  },
});

const DialogActions = styled(MuiDialogActions)({
  display: "flex",
  padding: "30px",
});

const DialogContent = styled(MuiDialogContent)(({ theme }) => ({
  minHeight: 300,
  "&::-webkit-scrollbar": {
    width: "0.4em",
  },
  "&::-webkit-scrollbar-track": {
    boxShadow: "inset 0 0 6px rgba(0,0,0,0.00)",
    webkitBoxShadow: "inset 0 0 6px rgba(0,0,0,0.00)",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: theme.palette.grey["300"],
    borderRadius: "2.5px",
  },
  margin: "0px 30px",
  padding: "unset",
  borderBottom: `1px solid ${theme.palette.grey["300"]}`,
  borderTop: `1px solid ${theme.palette.grey["300"]}`,
  overflowY: "initial",
}));
const useStyles = makeStyles((theme: Theme) => ({
  root: {
    marginLeft: "1px",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  icon: {
    borderRadius: "50%",
    width: 14,
    height: 14,
    border: `1px solid ${theme.palette.grey["A100"]}`,
    backgroundColor: theme.palette.common.white,
  },
  checkedIcon: {
    backgroundColor: theme.palette.primary.main,
  },
  attendRadio: {
    border: "2px solid #000 !important",
  },
}));

const StyledRadio = (props: RadioProps): React.ReactElement => {
  const classes = useStyles();

  return (
    <Radio
      focusVisibleClassName={classes.attendRadio}
      className={classes.root}
      tabIndex={0}
      // disableRipple
      color="default"
      checkedIcon={<span className={clsx(classes.icon, classes.checkedIcon)} />}
      icon={<span className={classes.icon} />}
      {...props}
    />
  );
};

const RadioField = styled(Box)(({ theme }) => ({
  marginTop: "27px",
  minWidth: "500px",
  "& fieldset": {
    fontFamily: "Arial",
    fontSize: "14px",
    color: theme.palette.grey["800"],
    letterSpacing: 0,
    fontWeight: 400,
    marginBottom: "13px",
    " & div": {
      float: "right",
    },
  },
  "& div": {
    "& label": {
      paddingLeft: "5px",
      margin: 0,
    },
  },
}));

const SurveyTitle = styled(Box)(({ theme }) => ({
  padding: "0px 45px 0px 30px",
  "& > span:first-of-type div": {
    fontSize: "30px",
    color: theme.palette.grey["800"],
    letterSpacing: 0,
    fontWeight: "bold",
    fontFamily: "Arial",
  },
  "& > span:nth-of-type(2) div": {
    fontSize: "19px",
    color: theme.palette.grey["500"],
    letterSpacing: 0,
    lineHeight: "16px",
    fontWeight: "bold",
    fontFamily: "Arial",
  },
}));

export const SurveyDialog = ({
  open,
  handleClose,
  onSubmit,
  handleSkipSurvey,
}: {
  open: boolean;
  handleClose: () => void;
  onSubmit: () => void;
  handleSkipSurvey?: () => void;
}): React.ReactElement => {
  const theme = useTheme();
  const { isMobile } = useViewport();
  const [surveyData, setSurveyData] = useState<SurveyTypes.SurveyData | null>(null);
  const [surveyId, setSurveyId] = useState<SaveSurveyInput>();
  const { loading: isSurveyLoading, data } = useQuery<SurveyTypes.SurveyData>(SURVEY_DATA, {
    variables: {
      surveyType: SurveyType.LEAP,
    },
    notifyOnNetworkStatusChange: true,
  });
  const [learnerAnswerArray, setLearnerAnswerArray] = useState([]);
  const [LearnerCommentMandatoryValidation, setIsLearnerCommentMandatory] = useState({
    isLearnerCommentMandatory: false,
  });
  const [learnerTextAreaComment, setLearnerTextAreaComment] = useState("");
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [saveSurvey] = useMutation(SAVE_SURVEY);
  const handleCancel = () => {
    handleDialogClose();
    handleSkipSurvey?.();
  };
  const submitSurvey = () => {
    if (learnerTextAreaComment) {
      const learnerAnswer = learnerAnswerArray.find(
        (p) => p.questionType == SURVEY_QUESTION_TYPE.ONLYComment,
      );
      if (learnerAnswer) {
        learnerAnswer.learnerComment = learnerTextAreaComment;
      }
    }
    const surveyObj: SaveSurveyInput = {
      surveyId: surveyId,
      learnerAnswers: learnerAnswerArray?.map((item) => {
        return {
          key: item.questionId,
          value: {
            optionId: item.optionId,
            learnerComment: item.learnerComment,
          },
        };
      }),
    };
    handleClose();
    saveSurvey({ variables: { surveyAnswer: surveyObj } })
      .then(() => {
        onSubmit();
        dispatch({
          type: "alert/show",
          payload: {
            type: "success",
            title: t("survey_submit_title"),
            message: t("survey_submit_success_alert_message"),
          },
        });
      })
      .catch(() => {
        dispatch({
          type: "alert/show",
          payload: {
            type: "error",
            title: t("survey_submit_title"),
            message: t("survey_submit_error_alert_message"),
          },
        });
      });
  };

  function validateMinCharacterCountForLearner() {
    const minCharacterCount: number =
      portalSettingsManager.surveyCharacterMinMaxThreshold?.surveyLearnerCommentMinLength;
    let isMinCharacterValidation = true;
    if (learnerTextAreaComment.trim().length > 0) {
      const newComment = learnerTextAreaComment.replace(removeSpaceAndSpecialCharacters, "");
      isMinCharacterValidation = !(newComment.length < minCharacterCount);
    }
    return isMinCharacterValidation;
  }

  const validateMaxCharacterCountForLearner = () => {
    const maxCharacterCount: number =
      portalSettingsManager.surveyCharacterMinMaxThreshold?.surveyLearnerCommentMaxLength;
    let isMaxCharacterValidation = true;
    if (learnerTextAreaComment.trim().length > 0) {
      const newComment = learnerTextAreaComment.replace(removeSpaceAndSpecialCharacters, "");
      isMaxCharacterValidation = !(newComment.length > maxCharacterCount);
    }
    return isMaxCharacterValidation;
  };

  const mapLearnerResponse = (
    selectedQuestion: any,
    type: string,
    value: string,
    questionType: string,
  ) => {
    if (questionType == SURVEY_QUESTION_TYPE.ONLYMCQ) {
      const selectedOptionArr = value.split("+");
      selectedQuestion.optionId = selectedOptionArr[0];
      selectedQuestion.optionText = selectedOptionArr[1];

      selectedQuestion.isLearnerCommentMandatory =
        selectedQuestion.optionText == SURVEY_OPTIONS_NEGATIVE_RESPONSE.NeitherAgreeOrDisagree ||
        selectedQuestion.optionText == SURVEY_OPTIONS_NEGATIVE_RESPONSE.StronglyDisagree ||
        selectedQuestion.optionText == SURVEY_OPTIONS_NEGATIVE_RESPONSE.Disagree;
    }
    return selectedQuestion;
  };

  const handleChange = (event: any, questionId: unknown, questionType: string) => {
    const commentQuestion = learnerAnswerArray.find(
      (p) => p.questionType == SURVEY_QUESTION_TYPE.ONLYComment,
    );

    if (commentQuestion == null) {
      let commentQuestionId = "";
      surveyData?.survey?.questions?.map((question) => {
        if (question.questionType == SURVEY_QUESTION_TYPE.ONLYComment) {
          commentQuestionId = question.questionId;
        }
      });
      learnerAnswerArray.push({
        questionId: commentQuestionId,
        questionType: SURVEY_QUESTION_TYPE.ONLYComment,
      });
    }
    let selectedQuestion = learnerAnswerArray.find((p) => p.questionId == questionId);
    if (selectedQuestion) {
      selectedQuestion = mapLearnerResponse(
        selectedQuestion,
        event.currentTarget.type,
        event.target.value,
        questionType,
      );
    } else {
      selectedQuestion = mapLearnerResponse(
        { questionId: questionId, questionType: questionType },
        event.currentTarget.type,
        event.target.value,
        questionType,
      );
      learnerAnswerArray.push(selectedQuestion);
    }
    setLearnerAnswerArray(learnerAnswerArray);
    const isCommentMandatory = learnerAnswerArray?.some((p) => p.isLearnerCommentMandatory);
    setIsLearnerCommentMandatory({ isLearnerCommentMandatory: isCommentMandatory });
  };
  useEffect(() => {
    if (data) {
      setSurveyId(data.survey?.surveyId);
      setSurveyData(data);
    }
  }, [data]);

  const handleDialogClose = () => {
    setLearnerAnswerArray([]);
    setLearnerTextAreaComment("");
    setIsLearnerCommentMandatory({ isLearnerCommentMandatory: false });
    handleClose();
  };

  const validateSelection = () => {
    return (
      surveyData?.survey?.questions?.length == learnerAnswerArray.length &&
      (!LearnerCommentMandatoryValidation.isLearnerCommentMandatory || learnerTextAreaComment) &&
      validateMinCharacterCountForLearner() &&
      validateMaxCharacterCountForLearner()
    );
  };

  const GetElementByQuestionType = (): React.ReactElement[] => {
    const elementToRender = [];
    surveyData?.survey?.questions?.map((item, index) => {
      if (item.questionType == SURVEY_QUESTION_TYPE.ONLYMCQ) {
        elementToRender.push(
          <RadioField key={index}>
            <FormControl component="fieldset" aria-disabled="false">
              <FormLabel
                component="legend"
                key={index}
                style={{ paddingBottom: "13px", maxWidth: isMobile ? "70vw" : "500px" }}
                aria-label={`${item?.htmlText ?? ""} ${t("aria_label_madatory_field")}`}
                aria-required={true}
              >
                {
                  <legend
                    style={{
                      fontSize: "14px",
                      color: theme.palette.grey["800"],
                      paddingLeft: "15px",
                      textIndent: "-15px",
                      lineHeight: "21px",
                    }}
                  >
                    <RenderHTML content={item?.htmlText ?? ""} />
                  </legend>
                }
              </FormLabel>
              <RadioGroup
                name={`${t("question").toLowerCase()} ${index + 1}`}
                onChange={(e) => handleChange(e, item?.questionId, item?.questionType)}
              >
                {item?.options?.map((option, optionIndex) => {
                  return (
                    <FormControlLabel
                      key={optionIndex}
                      value={`${option?.optionId}+${option?.htmlText}`}
                      title={option?.htmlText}
                      aria-required="true"
                      aria-label={` ${option?.htmlText ?? ""}`}
                      control={<StyledRadio />}
                      label={
                        <div
                          style={{
                            fontSize: "14px",
                            color: theme.palette.grey["800"],
                            lineHeight: "21px",
                          }}
                        >
                          <RenderHTML content={option?.htmlText ?? ""} />
                        </div>
                      }
                    />
                  );
                })}
              </RadioGroup>
            </FormControl>
          </RadioField>,
        );
      }
      if (item.questionType == SURVEY_QUESTION_TYPE.MCQWithComment) {
        elementToRender.push(
          <div>
            <RadioField key={index}>
              <FormControl component="fieldset" aria-disabled="false">
                <FormLabel
                  component="legend"
                  key={index}
                  style={{ paddingBottom: "13px", maxWidth: isMobile ? "70vw" : "500px" }}
                  aria-label={`${item?.htmlText ?? ""} ${t("aria_label_madatory_field")}`}
                  aria-required={true}
                >
                  {
                    <legend
                      style={{
                        fontSize: "14px",
                        color: theme.palette.grey["800"],
                        paddingLeft: "15px",
                        textIndent: "-15px",
                        lineHeight: "21px",
                      }}
                    >
                      <RenderHTML content={item?.htmlText ?? ""} />
                    </legend>
                  }
                </FormLabel>
                <RadioGroup
                  name={`${t("question").toLowerCase()} ${index + 1}`}
                  onChange={(e) => handleChange(e, item?.questionId, item.questionType)}
                >
                  {item?.options?.map((option, optionIndex) => {
                    return (
                      <FormControlLabel
                        key={optionIndex}
                        value={option?.optionId}
                        aria-required="true"
                        aria-label={` ${option?.htmlText ?? ""}`}
                        control={<StyledRadio />}
                        label={
                          <div
                            style={{
                              fontSize: "14px",
                              color: theme.palette.grey["800"],
                              lineHeight: "21px",
                            }}
                          >
                            <RenderHTML content={option?.htmlText ?? ""} />
                          </div>
                        }
                      />
                    );
                  })}
                </RadioGroup>
              </FormControl>
            </RadioField>

            <TextAreaComponent
              isMobile={isMobile}
              value={learnerTextAreaComment}
              name={`${t("question").toLowerCase()} ${index + 1}`}
              onChange={(event: { target: { value: SetStateAction<string> } }) =>
                setLearnerTextAreaComment(event.target.value)
              }
              rows={5}
            />
          </div>,
        );
      }
      if (item.questionType == SURVEY_QUESTION_TYPE.ONLYComment) {
        elementToRender.push(
          <div>
            <FormControl component="fieldset" aria-disabled="false">
              <FormLabel
                component="legend"
                key={index}
                style={{ paddingBottom: "13px", maxWidth: isMobile ? "70vw" : "500px" }}
                aria-label={`${item?.htmlText ?? ""} ${t("aria_label_madatory_field")}`}
                aria-required={true}
              >
                {
                  <legend
                    style={{
                      fontSize: "14px",
                      color: theme.palette.grey["800"],
                      paddingLeft: "15px",
                      textIndent: "-15px",
                      lineHeight: "21px",
                    }}
                  >
                    <RenderHTML content={item?.htmlText ?? ""} />
                  </legend>
                }
              </FormLabel>
            </FormControl>

            {LearnerCommentMandatoryValidation.isLearnerCommentMandatory &&
            !learnerTextAreaComment ? (
              <ErrorMessage>
                <img style={{ width: 10, marginRight: 5 }} alt={t("error_icon")} src={errorIcon} />{" "}
                {t("survey_learner_comment_required_message")}
              </ErrorMessage>
            ) : (
              ""
            )}
            {!validateMinCharacterCountForLearner() ? (
              <ErrorMessage>
                <img style={{ width: 10, marginRight: 5 }} alt={t("error_icon")} src={errorIcon} />{" "}
                {t("survey_learner_comment_min_character_message", {
                  min_character: t(
                    portalSettingsManager?.surveyCharacterMinMaxThreshold?.surveyLearnerCommentMinLength?.toString(),
                  ),
                })}
              </ErrorMessage>
            ) : (
              ""
            )}
            {!validateMaxCharacterCountForLearner() ? (
              <ErrorMessage>
                <img style={{ width: 10, marginRight: 5 }} alt={t("error_icon")} src={errorIcon} />{" "}
                {t("survey_learner_comment_max_character_message", {
                  max_character: t(
                    portalSettingsManager?.surveyCharacterMinMaxThreshold?.surveyLearnerCommentMaxLength?.toString(),
                  ),
                })}
              </ErrorMessage>
            ) : (
              ""
            )}
            <TextAreaComponent
              isMobile={isMobile}
              value={learnerTextAreaComment}
              onChange={(event: { target: { value: SetStateAction<string> } }) =>
                setLearnerTextAreaComment(event.target.value)
              }
              rows={5}
            />
          </div>,
        );
      }
    });
    return elementToRender;
  };
  const focusClass = useFocusStyles();
  const DialogButton = styled(Button)(({ theme }) => ({
    borderRadius: 0,
    fontSize: "11px",
    fontWeight: 700,
    lineHeight: "12px",
    height: "42px",
    width: "143px",
    border: `1px solid ${
      portalSettingsManager.buttonColors?.themed?.active?.border || theme.palette.primary.main
    }`,
    color: portalSettingsManager.buttonColors?.themed?.active?.text || theme.palette.primary.main,
    backgroundColor: portalSettingsManager.buttonColors?.themed?.active?.back,
    "&.Mui-disabled": {
      border: `1px solid ${theme.palette.primary.main}`,
    },
  }));

  return (
    <Dialog
      id="surdialog"
      onClose={handleDialogClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="dialog1_label"
      open={open}
      minWidth={!isMobile ? 724 : 0}
      maxHeight={isMobile ? 0 : 742}
      minHeight={!isMobile ? 660 : 0}
      fullScreen={isMobile}
      showCloseIcon
    >
      <DialogTitle style={{ minWidth: isMobile ? "79vw" : "660px" }} aria-required="true">
        <SurveyTitle>
          <span id="dialog1_label" role="heading" aria-level={2}>
            <p>
              <RenderHTML content={surveyData?.survey?.title || ""} />
            </p>
          </span>
          <span role="heading" aria-level={3}>
            <p>
              <RenderHTML content={surveyData?.survey?.htmlText || ""} />
            </p>
          </span>
        </SurveyTitle>
        <Box style={{ padding: "0px 0px 0px 30px" }}>
          <Box
            style={{
              // borderBottom: `1px solid ${theme.palette.grey['300']}`,
              fontSize: "12px",
              color: theme.palette.primary.main,
              letterSpacing: 0,
              fontWeight: 700,
            }}
          >
            {surveyData && (
              <p style={{ fontWeight: "bold", fontSize: "1.17em" }} role="heading" aria-level={4}>
                {surveyData?.survey?.questions?.length} {t("questions")}
              </p>
            )}
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent
        style={{
          display: "flex",
          flexDirection: "column",
          overflowX: isMobile ? "hidden" : "auto",
        }}
      >
        <Box>
          <Loader isDisplay={isSurveyLoading} type="component" message={t("survey_loading")} />
          {GetElementByQuestionType()}
        </Box>
      </DialogContent>
      <DialogActions>
        <Action type="dialog-alt-contrast" onClick={handleCancel}>
          {t("cancel_and_close")}
        </Action>
        <DialogButton
          variant="outlined"
          className={focusClass.focusItem}
          onClick={submitSurvey}
          disabled={!validateSelection()}
        >
          {t("surveysubmit_button")}
        </DialogButton>
      </DialogActions>
    </Dialog>
  );
};
